import React from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, DialogActions } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InfoIcon from "@mui/icons-material/Info";
import ModalHeader from "../../../../../../components/Modals/ModalControls/ModalHeader";
import ModalAccordion from "../../../../../../components/Modals/ModalAccordion";
import { ManagementInfo, AddressInfo } from "../../config/mgmtDataConfig";
import RenderFields from "../../../../../../components/Modals/ModalFields/RenderFields";
import ManagementContactTable from "./Contacts/ManagementContactTable";
import ManagementCustomerTable from "./Customers/ManagementCustomerTable";
import InformationDisplay from "./InformationDisplay";
import AlertSnackbar from "../../../../../../components/Notifications/AlertSnackbar";
import useViewModalLogic from "../../hooks/useViewModalLogic";

const ViewModal = ({
  open,
  handleClose,
  handleOpenAddFromView,
  updateList,
}) => {
  const {
    isEditMode,
    setIsEditMode,
    formData,
    handleFieldChange,
    handleSave,
    handleToggleEdit,
    validationErrors,
    isSubmitting,
    navHeight,
    isInfoOpen,
    handleInfoClick,
    newEntityMode,
    setNewEntityMode,
    newCustomerId,
    setNewCustomerId,
    showNewContactForm,
    setShowNewContactForm,
    autosaveStatus,
    isExpanded,
    setIsExpanded,
    snackbar,
    handleSnackbarClose,
    handleDelete,
    handleCloseModal,
    handlePrev,
    handleNext,
    contactForm,
    handleContactChange,
    handleOpenNew,
  } = useViewModalLogic(updateList, handleClose);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseModal}
        maxWidth={false}
        fullWidth
        PaperProps={{
          style: {
            position: "absolute",
            top: navHeight,
            left: "50%",
            transform: "translateX(-50%)",
            margin: 4,
            transition: "width 0.3s ease, height 0.3s ease",
            width: isExpanded ? "90%" : "1000px",
            height: `calc(100vh - ${navHeight + 8}px)`,
            overflow: "auto",
          },
        }}
        style={{ zIndex: 1400 }}
      >
        <ModalHeader
          isExpanded={isExpanded}
          setIsExpanded={() => setIsExpanded((prev) => !prev)}
          handleCloseModal={handleCloseModal}
          isEditMode={isEditMode}
          handleEditButtonClick={handleToggleEdit}
          handleCancel={() => setIsEditMode(false)}
          handleDelete={handleDelete}
          isSaving={isSubmitting || autosaveStatus === "Saving..."}
          autosaveStatus={autosaveStatus}
          handleOpenAddModal={handleOpenAddFromView}
        />
        <div style={{ width: "90%", margin: "0 auto" }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-3xl mb-4">Management Company</h2>
            <button
              onClick={handleInfoClick}
              className={`p-2 bg-white hover:bg-gray-200 transition ${
                isInfoOpen ? "text-sss-info" : "text-gray-800"
              }`}
            >
              {isInfoOpen ? <InfoIcon /> : <InfoOutlinedIcon />}
            </button>
          </div>
          <hr />
        </div>
        <DialogContent
          style={{
            minHeight: "500px",
            width: "90%",
            margin: "0 auto",
            padding: 0,
            height: "100%",
          }}
          sx={{ p: 0, pr: 2, pt: 2 }}
        >
          <div className="flex h-full transition-all duration-300">
            <div
              className={`transition-all duration-300 ${
                isInfoOpen ? "w-5/12" : "w-full"
              } h-full overflow-y-auto`}
            >
              {validationErrors.form && (
                <div className="text-red-500 mb-4">{validationErrors.form}</div>
              )}
              <ModalAccordion label="General" defaultExpanded>
                <div className="flex flex-col md:flex-row gap-4 w-full">
                  <RenderFields
                    fields={ManagementInfo}
                    formData={formData}
                    handleChange={handleFieldChange}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                  />
                </div>
              </ModalAccordion>
              <ModalAccordion label="Address" defaultExpanded>
                <div className="flex flex-col md:flex-row gap-4 w-full">
                  <RenderFields
                    fields={AddressInfo}
                    formData={formData}
                    handleChange={handleFieldChange}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                  />
                </div>
              </ModalAccordion>
              <ModalAccordion label="Contacts" defaultExpanded>
                <ManagementContactTable
                  contactForm={contactForm}
                  handleContactChange={handleContactChange}
                  isEditMode={isEditMode}
                  onAddNew={() => handleOpenNew("contact")}
                  onRowClick={undefined}
                  setShowNewContactForm={setShowNewContactForm}
                />
              </ModalAccordion>
              <ModalAccordion label="Customers" defaultExpanded>
                <ManagementCustomerTable
                  managementID={formData.managementId || 0}
                  isEditMode={isEditMode}
                  onAddNew={() => handleOpenNew("customer")}
                  // Pass the required new customer setters:
                  newCustomerId={newCustomerId}
                  setNewCustomerId={setNewCustomerId}
                />
              </ModalAccordion>
            </div>
            <div
              className={`transition-all duration-300 overflow-hidden ${
                isInfoOpen ? "w-7/12 opacity-100" : "w-0 opacity-0"
              } h-full`}
            >
              <div className="h-full overflow-y-auto mt-4 ml-4">
                <div className="p-4">
                  <InformationDisplay
                    isEditMode={isEditMode}
                    newMode={newEntityMode.active}
                    newEntityType={newEntityMode.type}
                    onCloseNewMode={() =>
                      setNewEntityMode({ active: false, type: null })
                    }
                    updateList={updateList}
                  />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div style={{ width: "90%", margin: "0 auto" }}>
          <hr />
        </div>
        <DialogActions
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#fff",
            zIndex: 1500,
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={handlePrev}
            className="p-2 bg-white hover:bg-gray-200 transition"
          >
            <ArrowBackIcon />
          </button>
          <button
            onClick={handleNext}
            className="p-2 mr-2 bg-white hover:bg-gray-200 transition"
          >
            <ArrowForwardIcon />
          </button>
        </DialogActions>
      </Dialog>
      <AlertSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        autoHideDuration={3000}
      />
    </>
  );
};

ViewModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  updateList: PropTypes.func,
  handleOpenAddFromView: PropTypes.func,
};

export default ViewModal;
